import React from 'react';
import {Save, CheckCircle, Heart, Lightbulb, Users} from 'lucide-react';

/**
 * Self-assessment component for students to reflect on their work
 *
 * @param {Object} props
 * @param {Object} props.selfAssessmentForm - Form data for self-assessment
 * @param {Function} props.handleSelfAssessmentChange - Function to handle form field changes
 * @param {Function} props.handleNeededChange - Function to handle "needed" multi-select changes
 * @param {Function} props.handleSelfAssessmentSubmit - Function to handle form submission
 * @param {Object} props.submission - Submission data if available
 */
const SelfAssessment = ({
                            selfAssessmentForm,
                            handleSelfAssessmentChange,
                            handleNeededChange,
                            handleSelfAssessmentSubmit,
                            submission
                        }) => {
    // If already submitted, show the submitted assessment
    if (submission && submission.self_assessment) {
        return (
            <div className="bg-white font-[poppins] rounded-2xl shadow-md overflow-hidden border border-gray-200 mb-8">
                {/* Header */}
                <div className="p-6 border-b border-gray-100 bg-gradient-to-r from-blue-50 to-indigo-50">
                    <div className="flex items-center gap-4">
                        <div className="w-12 h-12 rounded-xl flex items-center justify-center bg-[#D67C2C] shadow-lg">
                            <CheckCircle className="w-6 h-6 text-white"/>
                        </div>
                        <div>
                            <h2 className="text-2xl font-bold text-gray-900">Self-Assessment Complete</h2>
                            <p className="text-gray-600 mt-1">Your reflection has been submitted successfully</p>
                        </div>
                    </div>
                </div>

                <div className="p-8">
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                        {/* "I liked doing this work" section */}
                        <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-2xl p-6 border border-green-100 shadow-sm">
                            <div className="flex items-center gap-3 mb-6">
                                <div className="w-10 h-10 rounded-xl bg-green-500 flex items-center justify-center shadow-lg">
                                    <Heart className="w-5 h-5 text-white"/>
                                </div>
                                <h3 className="text-lg font-semibold text-gray-900">Enjoyment Level</h3>
                            </div>
                            
                            <div className="space-y-4">
                                <p className="text-sm text-gray-600 mb-4">I liked doing this work</p>
                                <div className="flex justify-center gap-6">
                                    <div className="flex flex-col items-center">
                                        <div className={`w-16 h-16 rounded-2xl flex items-center justify-center text-3xl transition-all shadow-lg
                        ${submission.self_assessment.liked === true ? 'bg-white border-2 border-green-500 scale-110' : 'bg-green-100'}`}>
                                            <span>😊</span>
                                        </div>
                                        <span className="mt-2 font-medium text-sm text-gray-700">Yes</span>
                                    </div>

                                    <div className="flex flex-col items-center">
                                        <div className={`w-16 h-16 rounded-2xl flex items-center justify-center text-3xl transition-all shadow-lg
                        ${submission.self_assessment.liked === false ? 'bg-white border-2 border-green-500 scale-110' : 'bg-green-100'}`}>
                                            <span>😔</span>
                                        </div>
                                        <span className="mt-2 font-medium text-sm text-gray-700">No</span>
                                    </div>

                                    <div className="flex flex-col items-center">
                                        <div className={`w-16 h-16 rounded-2xl flex items-center justify-center text-3xl transition-all shadow-lg
                        ${submission.self_assessment.liked === null ? 'bg-white border-2 border-green-500 scale-110' : 'bg-green-100'}`}>
                                            <span>🤔</span>
                                        </div>
                                        <span className="mt-2 font-medium text-sm text-gray-700 text-center">Don't<br/>Know</span>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* "I found this work easy" section */}
                        <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-2xl p-6 border border-purple-100 shadow-sm">
                            <div className="flex items-center gap-3 mb-6">
                                <div className="w-10 h-10 rounded-xl bg-purple-500 flex items-center justify-center shadow-lg">
                                    <Lightbulb className="w-5 h-5 text-white"/>
                                </div>
                                <h3 className="text-lg font-semibold text-gray-900">Difficulty Level</h3>
                            </div>
                            
                            <div className="space-y-4">
                                <p className="text-sm text-gray-600 mb-4">I found this work easy</p>
                                <div className="flex justify-center gap-6">
                                    <div className="flex flex-col items-center">
                                        <div className={`w-16 h-16 rounded-2xl flex items-center justify-center text-3xl transition-all shadow-lg
                                            ${submission.self_assessment.easy === true ? 'bg-white border-2 border-purple-500 scale-110' : 'bg-purple-100'}`}>
                                            <span>😊</span>
                                        </div>
                                        <span className="mt-2 font-medium text-sm text-gray-700">Yes</span>
                                    </div>
                                    <div className="flex flex-col items-center">
                                        <div className={`w-16 h-16 rounded-2xl flex items-center justify-center text-3xl transition-all shadow-lg
                                            ${submission.self_assessment.easy === false ? 'bg-white border-2 border-purple-500 scale-110' : 'bg-purple-100'}`}>
                                            <span>😔</span>
                                        </div>
                                        <span className="mt-2 font-medium text-sm text-gray-700">No</span>
                                    </div>
                                    <div className="flex flex-col items-center">
                                        <div className={`w-16 h-16 rounded-2xl flex items-center justify-center text-3xl transition-all shadow-lg
                                            ${submission.self_assessment.easy === null ? 'bg-white border-2 border-purple-500 scale-110' : 'bg-purple-100'}`}>
                                            <span>🤔</span>
                                        </div>
                                        <span className="mt-2 font-medium text-sm text-gray-700 text-center">Don't<br/>Know</span>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* "To do this work, I needed..." section */}
                        <div className="bg-gradient-to-br from-blue-50 to-cyan-50 rounded-2xl p-6 border border-blue-100 shadow-sm">
                            <div className="flex items-center gap-3 mb-6">
                                <div className="w-10 h-10 rounded-xl bg-blue-500 flex items-center justify-center shadow-lg">
                                    <Users className="w-5 h-5 text-white"/>
                                </div>
                                <h3 className="text-lg font-semibold text-gray-900">Support Needed</h3>
                            </div>
                            
                            <div className="space-y-4">
                                <p className="text-sm text-gray-600 mb-4">To do this work, I needed...</p>
                                <div className="grid grid-cols-3 gap-4">
                                    <div className="flex flex-col items-center">
                                        <div className={`w-14 h-14 rounded-xl flex items-center justify-center text-2xl transition-all shadow-lg
                                            ${submission.self_assessment.needed.includes("classmate") ? 'bg-white border-2 border-blue-500 scale-105' : 'bg-blue-100'}`}>
                                            <span>👥</span>
                                        </div>
                                        <span className="mt-2 font-medium text-xs text-center text-gray-700">Classmate</span>
                                    </div>

                                    <div className="flex flex-col items-center">
                                        <div className={`w-14 h-14 rounded-xl flex items-center justify-center text-2xl transition-all shadow-lg
                                            ${submission.self_assessment.needed.includes("teacher") ? 'bg-white border-2 border-blue-500 scale-105' : 'bg-blue-100'}`}>
                                            <span>👨‍🏫</span>
                                        </div>
                                        <span className="mt-2 font-medium text-xs text-center text-gray-700">Teacher</span>
                                    </div>

                                    <div className="flex flex-col items-center">
                                        <div className={`w-14 h-14 rounded-xl flex items-center justify-center text-2xl transition-all shadow-lg
                                            ${submission.self_assessment.needed.includes("books") ? 'bg-white border-2 border-blue-500 scale-105' : 'bg-blue-100'}`}>
                                            <span>📚</span>
                                        </div>
                                        <span className="mt-2 font-medium text-xs text-center text-gray-700">Books</span>
                                    </div>

                                    <div className="flex flex-col items-center">
                                        <div className={`w-14 h-14 rounded-xl flex items-center justify-center text-2xl transition-all shadow-lg
                                            ${submission.self_assessment.needed.includes("computer") ? 'bg-white border-2 border-blue-500 scale-105' : 'bg-blue-100'}`}>
                                            <span>💻</span>
                                        </div>
                                        <span className="mt-2 font-medium text-xs text-center text-gray-700">Computer</span>
                                    </div>

                                    <div className="flex flex-col items-center">
                                        <div className={`w-14 h-14 rounded-xl flex items-center justify-center text-2xl transition-all shadow-lg
                                            ${submission.self_assessment.needed.includes("none") ? 'bg-white border-2 border-blue-500 scale-105' : 'bg-blue-100'}`}>
                                            <span>⛔</span>
                                        </div>
                                        <span className="mt-2 font-medium text-xs text-center text-gray-700">None</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    // Otherwise, show the assessment form
    return (
        <div className="bg-white font-[poppins] rounded-2xl shadow-md overflow-hidden border border-gray-200 mb-8">
            {/* Header */}
            <div className="p-6 border-b border-gray-100 bg-gradient-to-r from-blue-50 to-indigo-50">
                <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-xl flex items-center justify-center bg-[#D67C2C] shadow-lg">
                        <CheckCircle className="w-6 h-6 text-white"/>
                    </div>
                    <div>
                        <h2 className="text-2xl font-bold text-gray-900">Self-Assessment</h2>
                        <p className="text-gray-600 mt-1">Reflect on your learning experience</p>
                    </div>
                </div>
            </div>

            <div className="p-8">
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                    {/* "I liked doing this work" section */}
                    <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-2xl p-6 border border-green-100 shadow-sm hover:shadow-md transition-all">
                        <div className="flex items-center gap-3 mb-6">
                            <div className="w-10 h-10 rounded-xl bg-green-500 flex items-center justify-center shadow-lg">
                                <Heart className="w-5 h-5 text-white"/>
                            </div>
                            <h3 className="text-lg font-semibold text-gray-900">Enjoyment Level</h3>
                        </div>
                        
                        <div className="space-y-4">
                            <p className="text-sm text-gray-600 mb-4">I liked doing this work</p>
                            <div className="flex justify-center gap-6">
                                <div className="flex flex-col items-center">
                                    <button
                                        type="button"
                                        onClick={() => handleSelfAssessmentChange('liked', true)}
                                        className={`w-16 h-16 rounded-2xl flex items-center justify-center text-3xl transition-all shadow-lg hover:scale-105
                        ${selfAssessmentForm.liked === true ? 'bg-white border-2 border-green-500 scale-110' : 'bg-green-100 hover:bg-white hover:border-2 hover:border-green-300'}`}
                                    >
                                        <span>😊</span>
                                    </button>
                                    <span className="mt-2 font-medium text-sm text-gray-700">Yes</span>
                                </div>

                                <div className="flex flex-col items-center">
                                    <button
                                        type="button"
                                        onClick={() => handleSelfAssessmentChange('liked', false)}
                                        className={`w-16 h-16 rounded-2xl flex items-center justify-center text-3xl transition-all shadow-lg hover:scale-105
                        ${selfAssessmentForm.liked === false ? 'bg-white border-2 border-green-500 scale-110' : 'bg-green-100 hover:bg-white hover:border-2 hover:border-green-300'}`}
                                    >
                                        <span>😔</span>
                                    </button>
                                    <span className="mt-2 font-medium text-sm text-gray-700">No</span>
                                </div>

                                <div className="flex flex-col items-center">
                                    <button
                                        type="button"
                                        onClick={() => handleSelfAssessmentChange('liked', null)}
                                        className={`w-16 h-16 rounded-2xl flex items-center justify-center text-3xl transition-all shadow-lg hover:scale-105
                        ${selfAssessmentForm.liked === null ? 'bg-white border-2 border-green-500 scale-110' : 'bg-green-100 hover:bg-white hover:border-2 hover:border-green-300'}`}
                                    >
                                        <span>🤔</span>
                                    </button>
                                    <span className="mt-2 font-medium text-sm text-gray-700 text-center">Don't<br/>Know</span>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* "I found this work easy" section */}
                    <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-2xl p-6 border border-purple-100 shadow-sm hover:shadow-md transition-all">
                        <div className="flex items-center gap-3 mb-6">
                            <div className="w-10 h-10 rounded-xl bg-purple-500 flex items-center justify-center shadow-lg">
                                <Lightbulb className="w-5 h-5 text-white"/>
                            </div>
                            <h3 className="text-lg font-semibold text-gray-900">Difficulty Level</h3>
                        </div>
                        
                        <div className="space-y-4">
                            <p className="text-sm text-gray-600 mb-4">I found this work easy</p>
                            <div className="flex justify-center gap-6">
                                <div className="flex flex-col items-center">
                                    <button
                                        type="button"
                                        onClick={() => handleSelfAssessmentChange('easy', true)}
                                        className={`w-16 h-16 rounded-2xl flex items-center justify-center text-3xl transition-all shadow-lg hover:scale-105
                        ${selfAssessmentForm.easy === true ? 'bg-white border-2 border-purple-500 scale-110' : 'bg-purple-100 hover:bg-white hover:border-2 hover:border-purple-300'}`}
                                    >
                                        <span>😊</span>
                                    </button>
                                    <span className="mt-2 font-medium text-sm text-gray-700">Yes</span>
                                </div>

                                <div className="flex flex-col items-center">
                                    <button
                                        type="button"
                                        onClick={() => handleSelfAssessmentChange('easy', false)}
                                        className={`w-16 h-16 rounded-2xl flex items-center justify-center text-3xl transition-all shadow-lg hover:scale-105
                        ${selfAssessmentForm.easy === false ? 'bg-white border-2 border-purple-500 scale-110' : 'bg-purple-100 hover:bg-white hover:border-2 hover:border-purple-300'}`}
                                    >
                                        <span>😔</span>
                                    </button>
                                    <span className="mt-2 font-medium text-sm text-gray-700">No</span>
                                </div>

                                <div className="flex flex-col items-center">
                                    <button
                                        type="button"
                                        onClick={() => handleSelfAssessmentChange('easy', null)}
                                        className={`w-16 h-16 rounded-2xl flex items-center justify-center text-3xl transition-all shadow-lg hover:scale-105
                        ${selfAssessmentForm.easy === null ? 'bg-white border-2 border-purple-500 scale-110' : 'bg-purple-100 hover:bg-white hover:border-2 hover:border-purple-300'}`}
                                    >
                                        <span>🤔</span>
                                    </button>
                                    <span className="mt-2 font-medium text-sm text-gray-700 text-center">Don't<br/>Know</span>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* "To do this work, I needed..." section */}
                    <div className="bg-gradient-to-br from-blue-50 to-cyan-50 rounded-2xl p-6 border border-blue-100 shadow-sm hover:shadow-md transition-all">
                        <div className="flex items-center gap-3 mb-6">
                            <div className="w-10 h-10 rounded-xl bg-blue-500 flex items-center justify-center shadow-lg">
                                <Users className="w-5 h-5 text-white"/>
                            </div>
                            <h3 className="text-lg font-semibold text-gray-900">Support Needed</h3>
                        </div>
                        
                        <div className="space-y-4">
                            <p className="text-sm text-gray-600 mb-4">To do this work, I needed... (Select all that apply)</p>
                            <div className="grid grid-cols-3 gap-4">
                                <div className="flex flex-col items-center">
                                    <button
                                        type="button"
                                        onClick={() => handleNeededChange("classmate")}
                                        className={`w-14 h-14 rounded-xl flex items-center justify-center text-2xl transition-all shadow-lg hover:scale-105
                          ${selfAssessmentForm.needed.includes("classmate") ? 'bg-white border-2 border-blue-500 scale-105' : 'bg-blue-100 hover:bg-white hover:border-2 hover:border-blue-300'}`}
                                    >
                                        <span>👥</span>
                                    </button>
                                    <span className="mt-2 font-medium text-xs text-center text-gray-700">Classmate</span>
                                </div>

                                <div className="flex flex-col items-center">
                                    <button
                                        type="button"
                                        onClick={() => handleNeededChange("teacher")}
                                        className={`w-14 h-14 rounded-xl flex items-center justify-center text-2xl transition-all shadow-lg hover:scale-105
                          ${selfAssessmentForm.needed.includes("teacher") ? 'bg-white border-2 border-blue-500 scale-105' : 'bg-blue-100 hover:bg-white hover:border-2 hover:border-blue-300'}`}
                                    >
                                        <span>👨‍🏫</span>
                                    </button>
                                    <span className="mt-2 font-medium text-xs text-center text-gray-700">Teacher</span>
                                </div>

                                <div className="flex flex-col items-center">
                                    <button
                                        type="button"
                                        onClick={() => handleNeededChange("books")}
                                        className={`w-14 h-14 rounded-xl flex items-center justify-center text-2xl transition-all shadow-lg hover:scale-105
                          ${selfAssessmentForm.needed.includes("books") ? 'bg-white border-2 border-blue-500 scale-105' : 'bg-blue-100 hover:bg-white hover:border-2 hover:border-blue-300'}`}
                                    >
                                        <span>📚</span>
                                    </button>
                                    <span className="mt-2 font-medium text-xs text-center text-gray-700">Books</span>
                                </div>

                                <div className="flex flex-col items-center">
                                    <button
                                        type="button"
                                        onClick={() => handleNeededChange("computer")}
                                        className={`w-14 h-14 rounded-xl flex items-center justify-center text-2xl transition-all shadow-lg hover:scale-105
                          ${selfAssessmentForm.needed.includes("computer") ? 'bg-white border-2 border-blue-500 scale-105' : 'bg-blue-100 hover:bg-white hover:border-2 hover:border-blue-300'}`}
                                    >
                                        <span>💻</span>
                                    </button>
                                    <span className="mt-2 font-medium text-xs text-center text-gray-700">Computer</span>
                                </div>

                                <div className="flex flex-col items-center">
                                    <button
                                        type="button"
                                        onClick={() => handleNeededChange("none")}
                                        className={`w-14 h-14 rounded-xl flex items-center justify-center text-2xl transition-all shadow-lg hover:scale-105
                          ${selfAssessmentForm.needed.includes("none") ? 'bg-white border-2 border-blue-500 scale-105' : 'bg-blue-100 hover:bg-white hover:border-2 hover:border-blue-300'}`}
                                    >
                                        <span>⛔</span>
                                    </button>
                                    <span className="mt-2 font-medium text-xs text-center text-gray-700">None</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Submit button */}
                <div className="flex justify-center mt-8 pt-6 border-t border-gray-200">
                    <button
                        type="button"
                        onClick={handleSelfAssessmentSubmit}
                        className="inline-flex items-center px-8 py-3 border border-transparent text-base font-medium rounded-xl shadow-lg text-white bg-[#D67C2C] hover:bg-[#c06a25] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#D67C2C] transition-all hover:scale-105"
                    >
                        <Save size={20} className="mr-3"/>
                        Save Self-Assessment
                    </button>
                </div>
            </div>
        </div>
    );
};

export default SelfAssessment;